// ============================================================
// View: Datenimport — echte Validierung, Vorschau, Ersetzen
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { Card, CardTitle } from '../components/ui';
import { Icon } from '../icons';
import { readableOnBrand, CO } from '../theme';
import { useApp } from '../store';
import { kpi, dataBounds } from '../aggregator';
import { dstr, eur, fmtNum, sEur } from '../format';
import { readFileAsText, validateAndNormalize, type ValidationIssue } from '../importer';

type Stage = 'idle' | 'validating' | 'error' | 'preview' | 'done';

const VALIDATION_RULES = [
  'Gültiges JSON und vorhandenes Root-Objekt',
  'eintraege ist ein Array',
  'Jeder Eintrag besitzt eine eindeutige ID',
  'abgabeIso ist ein gültiges Datum',
  'spielart ist vorhanden',
  'einsatzEuro ist eine gültige, nicht negative Zahl',
  'gewinnEuro ist eine gültige, nicht negative Zahl',
  'ergebnis.typ ist "gewinn" oder "verlust"',
  'status ist teilnehmend, in Auswertung, abgeschlossen oder archiviert',
];

export function Datenimport() {
  const { history, replaceHistory, clearHistory } = useApp();
  const [stage, setStage] = useState<Stage>('idle');
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [parsedSummary, setParsedSummary] = useState<{
    entries: import('../types').Eintrag[];
    arten: string[];
  } | null>(null);
  const [filename, setFilename] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const work = history?.eintraege ?? [];
  const b = dataBounds(work);
  const arten = Array.from(new Set(work.map((e) => e.spielart)));
  const k = kpi(work);
  const brand = CO.sidebar;
  const T = readableOnBrand(brand);

  useEffect(() => {
    if (stage === 'preview' && parsedSummary) {
      // Stelle sicher, dass die Vorschau angezeigt wird
    }
  }, [stage, parsedSummary]);

  async function processFile(file: File) {
    setFilename(file.name);
    setStage('validating');
    setIssues([]);
    try {
      const text = await readFileAsText(file);
      let json: unknown;
      try {
        json = JSON.parse(text);
      } catch {
        setIssues([{ loc: 'JSON-Parser', msg: 'Datei ist kein gültiges JSON.' }]);
        setStage('error');
        return;
      }
      const result = validateAndNormalize(json);
      if (!result.ok) {
        setIssues(result.issues);
        setStage('error');
        return;
      }
      const arts = Array.from(new Set(result.data!.eintraege.map((e) => e.spielart))).sort();
      setParsedSummary({ entries: result.data!.eintraege, arten: arts });
      setStage('preview');
    } catch (err) {
      setIssues([
        { loc: 'Datei', msg: err instanceof Error ? `Fehler beim Lesen: ${err.message}` : 'Unbekannter Lesefehler.' },
      ]);
      setStage('error');
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void processFile(f);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void processFile(f);
  }

  function commitImport() {
    if (!parsedSummary) return;
    const kk = kpi(parsedSummary.entries);
    const data = {
      exportiertAm: new Date().toISOString(),
      zusammenfassung: {
        einsatzGesamt: kk.einsatz,
        gewinnGesamt: kk.gewinn,
        saldo: kk.netto,
      },
      eintraege: parsedSummary.entries,
    };
    replaceHistory(data, parsedSummary.entries.length);
    setStage('done');
  }

  function reset() {
    setStage('idle');
    setIssues([]);
    setParsedSummary(null);
    setFilename('');
    if (inputRef.current) inputRef.current.value = '';
  }

  const previewK = parsedSummary ? kpi(parsedSummary.entries) : null;
  const previewBounds = parsedSummary ? dataBounds(parsedSummary.entries) : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>
      <div>
        <Card>
          <CardTitle title="Neue Spielhistorie importieren" sub="JSON-Export aus deinem Spielkonto" />
          <div style={{ fontSize: 13, color: CO.muted, lineHeight: 1.6, marginBottom: 16 }}>
            Ein neuer Import ersetzt den bisherigen Datensatz vollständig — aber{' '}
            <strong style={{ color: CO.text }}>nur dann, wenn die Datei gültig ist</strong>. Die Verarbeitung erfolgt
            ausschließlich lokal in diesem Browser; es werden keine Daten an einen Server übertragen.
          </div>

          {stage === 'idle' || stage === 'validating' ? (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${stage === 'validating' ? brand : isDragOver ? brand : CO.border}`,
                borderRadius: 14,
                padding: '40px 24px',
                textAlign: 'center',
                background: '#fafbf9',
                transition: 'border-color .2s',
              }}
            >
              {stage === 'validating' ? (
                <div>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      border: `3px solid ${CO.border}`,
                      borderTopColor: brand,
                      borderRadius: '50%',
                      margin: '0 auto 14px',
                      animation: 'wlSpin .8s linear infinite',
                    }}
                  />
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Datei wird geprüft …</div>
                  <div style={{ fontSize: 12.5, color: CO.muted, marginTop: 4 }}>
                    {filename || 'JSON-Datei'} wird gelesen und validiert.
                  </div>
                </div>
              ) : (
                <div>
                  <Icon name="down" color={brand} size={34} />
                  <div style={{ fontSize: 15, fontWeight: 600, marginTop: 12 }}>JSON-Datei hierher ziehen</div>
                  <div style={{ fontSize: 12.5, color: CO.muted, marginTop: 4, marginBottom: 16 }}>
                    oder Datei auswählen · akzeptierter Typ: .json
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={onPickFile}
                    style={{ display: 'none' }}
                    id="wl-import-file"
                  />
                  <label
                    htmlFor="wl-import-file"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: 9,
                      background: brand,
                      color: T.fg,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Datei auswählen
                  </label>
                </div>
              )}
            </div>
          ) : null}

          {stage === 'error' ? (
            <div
              style={{
                border: '1px solid #e6c4c1',
                background: '#fbeeed',
                borderRadius: 12,
                padding: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
                <Icon name="warn" color={CO.neg} size={18} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#9a332c' }}>
                  Import abgelehnt — Datei ist ungültig
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: CO.muted, marginBottom: 12 }}>
                Der bisherige Datensatz wurde nicht verändert. Bitte korrigieren Sie die folgenden Fehler:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {issues.map((er, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 10,
                      fontSize: 12.5,
                      background: '#fff',
                      border: '1px solid #f0dcda',
                      borderRadius: 8,
                      padding: '9px 12px',
                    }}
                  >
                    <span
                      style={{
                        color: CO.neg,
                        fontWeight: 700,
                        fontFamily: "'IBM Plex Mono',monospace",
                        flex: '0 0 auto',
                      }}
                    >
                      ×
                    </span>
                    <span className="num" style={{ color: '#9a332c', fontWeight: 600, flex: '0 0 190px' }}>
                      {er.loc}
                    </span>
                    <span style={{ color: CO.muted }}>{er.msg}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={reset}
                style={{
                  marginTop: 14,
                  padding: '9px 16px',
                  border: `1px solid ${CO.border}`,
                  borderRadius: 8,
                  background: '#fff',
                  color: CO.muted,
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                Erneut versuchen
              </button>
            </div>
          ) : null}

          {stage === 'preview' && parsedSummary && previewK && previewBounds ? (
            <div
              style={{
                border: '1px solid #cde3d0',
                background: '#f3f8f3',
                borderRadius: 12,
                padding: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
                <Icon name="check" color={CO.pos} size={18} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2b6b3d' }}>
                  Datei ist gültig — Vorschau vor dem Ersetzen
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: CO.muted, marginBottom: 14 }}>
                Prüfen Sie die erkannten Werte. Erst mit „Vorhandene Daten ersetzen" wird der aktuelle Datensatz
                überschrieben.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                {[
                  ['Anzahl Einträge', fmtNum(parsedSummary.entries.length)],
                  [
                    'Zeitraum',
                    `${dstr(previewBounds.min.toISOString())} – ${dstr(previewBounds.max.toISOString())}`,
                  ],
                  ['Gesamteinsatz', eur(previewK.einsatz)],
                  ['Gesamtauszahlung', eur(previewK.gewinn)],
                  ['Nettoergebnis', sEur(previewK.netto)],
                  ['Datei', filename || '—'],
                ].map(([label, val], i) => (
                  <div
                    key={i}
                    style={{
                      background: '#fff',
                      border: '1px solid #dcebdd',
                      borderRadius: 9,
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ fontSize: 11, color: CO.faint }}>{label}</div>
                    <div className="num" style={{ fontSize: 14, fontWeight: 700, color: CO.text }}>
                      {val}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: CO.muted, marginBottom: 12 }}>
                Erkannte Spielarten: {parsedSummary.arten.join(', ')}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={commitImport}
                  style={{
                    padding: '11px 18px',
                    border: 'none',
                    borderRadius: 9,
                    background: brand,
                    color: T.fg,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Vorhandene Daten ersetzen
                </button>
                <button
                  onClick={reset}
                  style={{
                    padding: '11px 18px',
                    border: `1px solid ${CO.border}`,
                    borderRadius: 9,
                    background: '#fff',
                    color: CO.muted,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : null}

          {stage === 'done' ? (
            <div
              style={{
                border: '1px solid #cde3d0',
                background: '#f3f8f3',
                borderRadius: 12,
                padding: 22,
                textAlign: 'center',
              }}
            >
              <Icon name="check" color={CO.pos} size={30} />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2b6b3d', marginTop: 8 }}>Datensatz ersetzt</div>
              <div style={{ fontSize: 12.5, color: CO.muted, marginTop: 4 }}>
                {fmtNum(parsedSummary?.entries.length ?? 0)} Einträge sind jetzt aktiv und lokal gespeichert.
              </div>
              <button
                onClick={reset}
                style={{
                  marginTop: 14,
                  padding: '10px 18px',
                  border: 'none',
                  borderRadius: 9,
                  background: brand,
                  color: T.fg,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Weiteren Import starten
              </button>
            </div>
          ) : null}
        </Card>

        {history ? (
          <Card style={{ marginTop: 16 }}>
            <CardTitle title="Aktuell aktiver Datensatz" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
              <div style={{ background: '#f6f7f4', borderRadius: 9, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: CO.faint }}>Einträge</div>
                <div className="num" style={{ fontSize: 14, fontWeight: 700 }}>
                  {fmtNum(work.length)}
                </div>
              </div>
              <div style={{ background: '#f6f7f4', borderRadius: 9, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: CO.faint }}>Zeitraum</div>
                <div className="num" style={{ fontSize: 13, fontWeight: 700 }}>
                  {dstr(b.min.toISOString())} – {dstr(b.max.toISOString())}
                </div>
              </div>
              <div style={{ background: '#f6f7f4', borderRadius: 9, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: CO.faint }}>Einsatz</div>
                <div className="num" style={{ fontSize: 14, fontWeight: 700 }}>
                  {eur(k.einsatz)}
                </div>
              </div>
              <div style={{ background: '#f6f7f4', borderRadius: 9, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: CO.faint }}>Spielarten</div>
                <div className="num" style={{ fontSize: 14, fontWeight: 700 }}>
                  {fmtNum(arten.length)}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                if (confirm('Aktiven Datensatz wirklich aus dem Browser entfernen?')) {
                  clearHistory();
                }
              }}
              style={{
                padding: '9px 14px',
                border: `1px solid ${CO.border}`,
                borderRadius: 8,
                background: '#fff',
                color: CO.neg,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Datensatz aus Browser entfernen
            </button>
          </Card>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Card>
          <CardTitle title="Validierung" sub="Vor dem Ersetzen geprüft" />
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
            }}
          >
            {VALIDATION_RULES.map((t, i) => (
              <li key={i} style={{ display: 'flex', gap: 9, fontSize: 12.5, color: CO.muted }}>
                <Icon name="check" color={CO.pos} size={15} /> {t}
              </li>
            ))}
          </ul>
        </Card>

        <div
          style={{
            background: brand,
            color: T.fg,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <Icon name="lock" color={T.fg} size={18} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>Lokale Speicherung</div>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: T.fg80 }}>
            Die Daten werden ausschließlich im localStorage dieses Browsers gehalten und beim erneuten Öffnen
            wiederhergestellt. Bei einem fehlgeschlagenen Import bleibt der bisherige Datensatz unverändert erhalten. Kein
            Backend, kein Login, keine Cloud-Synchronisierung.
          </div>
        </div>
      </div>
    </div>
  );
}
